import { EXPIRY_SKEW_MS, isSessionLive, type AdminSession } from "../../services/adminSession";

// Pure decisions for the admin-protected routes (/admin/*, /schedule). Kept separate from the
// React components so they can be unit-tested without a DOM. These only decide what to
// RENDER — the server independently rejects every /admin/* and /schedule API call that lacks a
// valid admin session, so a bypassed client guard exposes no data.

export type AdminGate =
  | { kind: "login" }
  | { kind: "render"; mustChangePassword: boolean };

export function decideAdminGate(args: { session: AdminSession | null; nowMs: number }): AdminGate {
  if (!isSessionLive(args.session, args.nowMs)) return { kind: "login" };
  return { kind: "render", mustChangePassword: args.session.mustChangePassword };
}

/** SPA route patterns that require the admin sign-in (the shell wraps exactly these). */
export const ADMIN_PROTECTED_ROUTES = ["/schedule", "/admin/*"] as const;

/** True when a browser pathname falls under an admin-protected SPA route. */
export function isAdminProtectedPath(pathname: string): boolean {
  const p = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  return ADMIN_PROTECTED_ROUTES.some((route) => {
    const base = route.replace(/\/\*$/, "");
    return route.endsWith("/*") ? p === base || p.startsWith(base + "/") : p === base;
  });
}

export const ADMIN_TABS = ["dashboard", "officers", "recordings", "settings"] as const;
export type AdminTab = typeof ADMIN_TABS[number];

/** Admin console tab addressed by the URL: /admin → dashboard, /admin/settings → settings. */
export function adminTabFromPath(pathname: string): AdminTab {
  const segment = pathname.toLowerCase().replace(/\/+$/, "").split("/")[2] ?? "";
  return (ADMIN_TABS as readonly string[]).includes(segment) ? (segment as AdminTab) : "dashboard";
}

/** Milliseconds until the session should be treated as expired (0 when already expired). */
export function msUntilExpiry(session: AdminSession, nowMs: number): number {
  const exp = Date.parse(session.expiresAt);
  return Number.isFinite(exp) ? Math.max(0, exp - EXPIRY_SKEW_MS - nowMs) : 0;
}
